import React, {useCallback} from 'react';
import {
  TBoolean,
  TEnum,
  TNumber,
  TRecord,
  TText,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {
  withSystem,
  Plugins,
  withMeta,
  type ExpressionPath,
} from '@packages/systems/wfdl';
import {
  ExpressionEditor,
  extendExpressionEditorActionAtPath,
} from '@packages/systems/ExpressionEditor-utils';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {
  ATTR_AUTOFOCUS_PATH,
  ATTR_ID_PATH,
  ATTR_PLACEHOLDER_PATH,
  ATTR_REQUIRED_PATH,
  ATTR_TYPE_PATH,
  FORM_DEFAULT_LABEL,
  FORM_PASSWORDPAGE_PATH,
  TEXT_TYPE_LABELS,
} from '../constants';
import {emotionStyled, tokens} from '@packages/utilities/styled';
import {
  useUpdateSiblingLabel,
  useUpdateFormNameDependencies,
} from './formHooks';
import {createDevLinkRootAlias} from '@packages/utilities/integrated-types';

const {getIn} = ContextualExpressions;

const Grid = emotionStyled('div')({
  display: 'grid',
  gap: tokens.space[2],
});

const formFields = withSystem(
  {labelVariant: 'hidden'},
  TRecord({
    name: withSystem(
      {label: 'Name', defaultValue: FORM_DEFAULT_LABEL, isLocalizable: true},
      TTypeConstructor(qualify(Plugins.name('Basic'), typeName('OnBlurText')))
    ),
    type: withSystem({defaultValue: 'input', private: true}, TText),
    passwordPage: withSystem({defaultValue: false, private: true}, TBoolean),
  })
);

const AttrFields = TRecord({
  id: withSystem(
    {
      defaultValue: FORM_DEFAULT_LABEL,
      private: true,
      label: 'ID',
      isBindable: false,
      isLocalizable: true,
    },
    TTypeConstructor(qualify(Plugins.name('Basic'), typeName('IdTextInput')))
  ),
  name: withSystem(
    {defaultValue: FORM_DEFAULT_LABEL, private: true, isLocalizable: true},
    TText
  ),
  maxlength: withSystem({defaultValue: 256, private: true}, TNumber),
  'data-name': withSystem(
    {defaultValue: FORM_DEFAULT_LABEL, private: true, isLocalizable: true},
    TText
  ),
  placeholder: withMeta(
    {placeholder: 'e.g. Enter your first name'},
    withSystem(
      {label: 'Placeholder', isFullWidth: true, isLocalizable: true},
      TText
    )
  ),
  disabled: withSystem({private: true}, TBoolean),
  type: withMeta(
    {
      variant: 'menu',
    },
    withMeta(
      {withoutLivePreview: true},
      withSystem(
        {label: 'Type', defaultValue: 'text', isLocalizable: true},
        TEnum(TEXT_TYPE_LABELS)
      )
    )
  ),
  required: withSystem(
    {
      defaultValue: false,
      label: 'Required',
      labelVariant: 'hidden',
      isLocalizable: true,
    },
    TTypeConstructor(
      qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
    )
  ),
  autofocus: withSystem(
    {
      defaultValue: false,
      label: 'Autofocus',
      labelVariant: 'hidden',
      isLocalizable: true,
    },
    TTypeConstructor(
      qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
    )
  ),
});

const fields = {
  form: formFields,
  attr: AttrFields,
} as const;

const getExpressionFromPath = (
  expr: ContextualExpression,
  path: ExpressionPath
) => {
  const expression = getIn(expr, path);
  if (!expression)
    throw Error(`failed to get expression at path: ${JSON.stringify(path)}`);
  return expression;
};

const FormTextInputType = createDevLinkRootAlias({
  params: ['extraAtomFields'],
  fields,
  render: function RenderFormTextInputRender({
    expr,
    dispatch,
  }: {
    expr: ContextualExpression;
    dispatch: any;
  }) {
    const [formNameExpr, setFormName] = useUpdateFormNameDependencies(
      expr,
      dispatch
    );

    const id =
      ContextualExpressions.getText(
        stripAnn(getExpressionFromPath(expr, ATTR_ID_PATH))
      ) || '';
    useUpdateSiblingLabel(id);

    const dispatchAt = extendExpressionEditorActionAtPath(dispatch);

    const passwordPageExpr = getIn(expr, FORM_PASSWORDPAGE_PATH);
    if (!passwordPageExpr)
      throw Error(
        `failed to get expression at path: ${JSON.stringify(
          FORM_PASSWORDPAGE_PATH
        )}`
      );
    const passwordPageExprStripped = stripAnn(passwordPageExpr);
    const isPasswordPage = ContextualExpressions.getBoolean(
      passwordPageExprStripped
    );

    const placeholderExpr = getExpressionFromPath(expr, ATTR_PLACEHOLDER_PATH);
    const placeholderDispatch = useCallback(dispatchAt(ATTR_PLACEHOLDER_PATH), [
      dispatchAt,
    ]);

    const textTypeExpr = getExpressionFromPath(expr, ATTR_TYPE_PATH);
    const textTypeDispatch = useCallback(dispatchAt(ATTR_TYPE_PATH), [
      dispatchAt,
    ]);

    const requiredExpr = getExpressionFromPath(expr, ATTR_REQUIRED_PATH);
    const requiredDispatch = useCallback(dispatchAt(ATTR_REQUIRED_PATH), [
      dispatchAt,
    ]);

    const autofocusExpr = getExpressionFromPath(expr, ATTR_AUTOFOCUS_PATH);
    const autofocusDispatch = useCallback(dispatchAt(ATTR_AUTOFOCUS_PATH), [
      dispatchAt,
    ]);

    return (
      <Grid>
        {/* Name */}
        {!isPasswordPage && (
          <ExpressionEditor expression={formNameExpr} dispatch={setFormName} />
        )}
        {/* TextType */}
        {!isPasswordPage && (
          <ExpressionEditor
            expression={textTypeExpr}
            dispatch={textTypeDispatch}
          />
        )}
        {/* Placeholder */}
        <ExpressionEditor
          expression={placeholderExpr}
          dispatch={placeholderDispatch}
        />
        <div>
          {/* Required */}
          {!isPasswordPage && (
            <ExpressionEditor
              expression={requiredExpr}
              dispatch={requiredDispatch}
            />
          )}
          {/* Autofocus */}
          <ExpressionEditor
            expression={autofocusExpr}
            dispatch={autofocusDispatch}
          />
        </div>
      </Grid>
    );
  },
});

export default FormTextInputType;
